/**
 * Where an old prospects page address lands now.
 *
 * The prospects pages were drawn by a separate app at `/prospector/<page>`
 * until 2026-10-24, and those addresses are in bookmarks, in sent emails and in
 * the browser helper. They are part of this app now, at `/prospecting/<page>`
 * and `/settings/prospecting` (docs/PROSPECTING-MERGE.md). Two places answer an
 * old address and both read this one table: the API's gateway answers a page
 * load with a 308, and the web router redirects a path that reached it inside
 * the app. One table, so the two can never send the same link to different
 * screens.
 *
 * WHAT IT WILL NOT DO is take a destination from the request. The location is
 * built from fixed strings, a page name from the list below, and a settings
 * sub path that may only hold letters, digits, dashes and slashes, then the
 * request's own query. A path it does not know answers null, which the
 * gateway turns into a 404 and the router into Page not found, so the mapping
 * cannot be steered into an open redirect.
 *
 * No imports, so the web takes it as `@gojibasel/shared/prospecting-paths`
 * without pulling the kernel's Node code into the browser.
 */

/** The old prefix, and the new homes. */
export const LEGACY_PROSPECTING_PREFIX = '/prospector'
export const PROSPECTING_PATH = '/prospecting'
export const PROSPECTING_SETTINGS_PATH = '/settings/prospecting'

/**
 * Every page the old app drew under `/prospector/<page>`, by the name it had
 * there, which is the name it keeps under `/prospecting/<page>`. `review` is not
 * here: it became the Queue's One at a time mode and has a rule of its own.
 */
export const PROSPECTING_PAGES = [
  'posts',
  'creators',
  'mentions',
  'competitors',
  'reviews',
  'prospects',
  'sequences',
  'trends',
  'results',
  'keywords',
  'x-search',
  'linkedin-search',
] as const

export type ProspectingPage = (typeof PROSPECTING_PAGES)[number]

const PAGES: ReadonlySet<string> = new Set(PROSPECTING_PAGES)

/** A settings sub path the mapping will carry: `helper`, `a/b`, never `//` or a dot segment. */
const SAFE_REST = /^(?:\/[A-Za-z0-9_-]+)+$/

/** `?a=1` or `''`, from a search string with or without its `?`. */
function query(search: string): string {
  const bare = search.startsWith('?') ? search.slice(1) : search
  return bare === '' ? '' : `?${bare}`
}

/**
 * The Queue's One at a time mode, with the old link's query after it.
 *
 * `mode=review` comes first and wins over a `mode` the old link carried,
 * because the address it came from named that mode by its path.
 */
export function reviewModeTarget(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete('mode')
  const rest = params.toString()
  return `${PROSPECTING_PATH}?mode=review${rest === '' ? '' : `&${rest}`}`
}

/**
 * The new address for an old `/prospector` page, or null when it is not one.
 *
 *   /prospector                 -> /prospecting
 *   /prospector/review          -> /prospecting?mode=review&...
 *   /prospector/settings[/x]    -> /settings/prospecting[/x]
 *   /prospector/login           -> /login (signing in is this app's own screen)
 *   /prospector/<page>          -> /prospecting/<page>
 *
 * The query travels, except to the sign-in screen, which reads none. A
 * trailing slash is ignored. `pathname` is the path alone, `search` the query
 * with or without its `?`.
 */
export function legacyProspectingTarget(pathname: string, search = ''): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (path === LEGACY_PROSPECTING_PREFIX) return `${PROSPECTING_PATH}${query(search)}`
  if (!path.startsWith(`${LEGACY_PROSPECTING_PREFIX}/`)) return null
  const rest = path.slice(LEGACY_PROSPECTING_PREFIX.length)
  if (rest === '/review') return reviewModeTarget(search)
  if (rest === '/login') return '/login'
  if (rest === '/settings') return `${PROSPECTING_SETTINGS_PATH}${query(search)}`
  if (rest.startsWith('/settings/')) {
    const sub = rest.slice('/settings'.length)
    return SAFE_REST.test(sub) ? `${PROSPECTING_SETTINGS_PATH}${sub}${query(search)}` : null
  }
  const page = rest.slice(1)
  return PAGES.has(page) ? `${PROSPECTING_PATH}/${page}${query(search)}` : null
}
